import React, {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  InputHTMLAttributes,
} from "react";
import INTL_TEL_INPUT from "intl-tel-input/build/js/intlTelInput.min.js";
import "intl-tel-input/build/js/utils.js";
import "intl-tel-input/build/css/intlTelInput.min.css";
import { CountryCode, getCountryCode } from "src/utils/meristemContext";
import { handleFocusBehindBanner } from "src/utils/payment";
import { css, Global } from "@emotion/react";
import { fontP4Regular } from "src/design-system/styles/fonts";
import { compassColors } from "src/utils/styling";

export function isValidNumber(number: string, countryCode: string) {
  return (window as any)?.intlTelInputUtils.isValidNumber(number, countryCode);
}

const US_NANP_MAX_DIGITS = 10;

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

/** Digits for storage; strips leading 1 when 11 digits. */
function usNanpDigits(value: string): string {
  let d = digitsOnly(value);
  if (d.length === 11 && d.startsWith("1")) {
    d = d.slice(1);
  }
  return d.slice(0, US_NANP_MAX_DIGITS);
}

/**
 * US national display for NANP digits only (no intl-tel-input).
 * Examples: "", "(2", "(234) 5", "(234) 567-8901"
 */
function formatUsNationalDisplay(digits: string): string {
  const d = usNanpDigits(digits);
  if (d.length === 0) {
    return "";
  }
  if (d.length <= 3) {
    return `(${d}`;
  }
  if (d.length <= 6) {
    return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  }
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function handleUsPlainKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
  if (e.ctrlKey || e.metaKey || e.altKey) {
    return;
  }
  if (e.key.length !== 1) {
    return;
  }
  if (!/[0-9]/.test(e.key)) {
    e.preventDefault();
    return;
  }
  const { value, selectionStart, selectionEnd } = e.currentTarget;
  const start = selectionStart ?? value.length;
  const end = selectionEnd ?? value.length;
  const selected = start !== end ? value.slice(start, end) : "";
  const digitsAfterKey =
    usNanpDigits(value).length - usNanpDigits(selected).length + 1;
  if (digitsAfterKey > US_NANP_MAX_DIGITS) {
    e.preventDefault();
  }
}

export type IntlTelInputProps = {
  id: string;
  className?: string;
  placeholder: string;
  onPhoneNumberUpdated: ({
    phoneNumber,
    phoneNumberCountryCode,
  }: {
    phoneNumber: string;
    phoneNumberCountryCode: string;
  }) => void;
  countryCode?: CountryCode;
  countryCodeDisabled?: boolean;
  /**
   * When `true` and `countryCode` is `US`: uses a plain input with reliable
   * `(111) 555-2345` formatting (intl-tel-input is not used — it conflicts
   * with React). Digits-only in callbacks, max 10 NANP digits, non-digits
   * blocked on keypress.
   */
  restrictUsPhoneInput?: boolean;
  /** Shown below the input on the US restricted path only (e.g. blur validation). */
  errorMessage?: React.ReactNode;
} & InputHTMLAttributes<HTMLInputElement>;

const usPhoneFieldErrorStyles = css`
  ${fontP4Regular}
  margin: 8px 0 0;
  color: ${compassColors.taroccoShade1};
`;

type UsRestrictedRest = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  | "id"
  | "className"
  | "placeholder"
  | "type"
  | "value"
  | "defaultValue"
  | "onChange"
  | "onKeyDown"
  | "onPaste"
  | "onFocus"
  | "maxLength"
  | "inputMode"
  | "autoComplete"
>;

/** US-only path: formatted display, digits-only to parent (no intl-tel-input). */
function UsRestrictedPhoneInput({
  id,
  className,
  placeholder,
  onPhoneNumberUpdated,
  value,
  userOnChange,
  userOnKeyDown,
  userOnPaste,
  userOnFocus,
  userMaxLength,
  userInputMode,
  errorMessage,
  restInputProps,
}: {
  id: string;
  className?: string;
  placeholder: string;
  onPhoneNumberUpdated: IntlTelInputProps["onPhoneNumberUpdated"];
  value: string | undefined | null;
  userOnChange?: React.ChangeEventHandler<HTMLInputElement>;
  userOnKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  userOnPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  userOnFocus?: React.FocusEventHandler<HTMLInputElement>;
  userMaxLength?: number;
  userInputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  errorMessage?: React.ReactNode;
  restInputProps: UsRestrictedRest;
}) {
  const digits = usNanpDigits(String(value ?? ""));
  const display = formatUsNationalDisplay(digits);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const next = usNanpDigits(e.target.value);
    onPhoneNumberUpdated({
      phoneNumber: next,
      phoneNumberCountryCode: "us",
    });
    userOnChange?.(e);
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const text = e.clipboardData?.getData("text/plain") ?? "";
    const { value: fieldValue, selectionStart, selectionEnd } = e.currentTarget;
    const start = selectionStart ?? fieldValue.length;
    const end = selectionEnd ?? fieldValue.length;
    const merged = `${fieldValue.slice(0, start)}${text}${fieldValue.slice(
      end
    )}`;
    const next = usNanpDigits(merged);
    onPhoneNumberUpdated({
      phoneNumber: next,
      phoneNumberCountryCode: "us",
    });
    userOnPaste?.(e);
  }

  return (
    <>
      <input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...restInputProps}
        id={id}
        className={className}
        type="tel"
        placeholder={placeholder}
        inputMode={userInputMode ?? "tel"}
        autoComplete="tel-national"
        maxLength={userMaxLength ?? 14}
        value={display}
        onChange={handleChange}
        onKeyDown={(e) => {
          handleUsPlainKeyDown(e);
          userOnKeyDown?.(e);
        }}
        onPaste={handlePaste}
        onFocus={(e) => {
          handleFocusBehindBanner(e);
          userOnFocus?.(e);
        }}
      />
      {errorMessage ? (
        <p css={usPhoneFieldErrorStyles} role="alert">
          {errorMessage}
        </p>
      ) : null}
    </>
  );
}

type IntlTelPluginProps = {
  id: string;
  className?: string;
  placeholder: string;
  onPhoneNumberUpdated: IntlTelInputProps["onPhoneNumberUpdated"];
  countryCode: CountryCode;
  countryCodeDisabled: boolean;
  mergedInputProps: InputHTMLAttributes<HTMLInputElement>;
  userOnChange?: React.ChangeEventHandler<HTMLInputElement>;
  userOnKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  userOnPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  userOnFocus?: React.FocusEventHandler<HTMLInputElement>;
};

function IntlTelPluginInput({
  id,
  className,
  placeholder,
  onPhoneNumberUpdated,
  countryCode,
  countryCodeDisabled,
  mergedInputProps,
  userOnChange,
  userOnKeyDown,
  userOnPaste,
  userOnFocus,
}: IntlTelPluginProps) {
  const inputRef = useRef(null);

  useEffect(() => {
    const intlTelInput = INTL_TEL_INPUT(inputRef.current, {
      preferredCountries: ["us", "ca", "gb"],
      initialCountry: "us",
      autoPlaceholder: countryCode === "US" ? "off" : "aggressive",
      allowDropdown: !countryCodeDisabled,
      showFlags: !countryCodeDisabled,
    });
    try {
      intlTelInput.setCountry(countryCode.toLowerCase());
    } catch (error) {
      /* NOP */
    }

    return function cleanup() {
      intlTelInput.destroy();
    };
  }, [countryCode, countryCodeDisabled, id]);

  function changeHandler(e: ChangeEvent<HTMLInputElement>) {
    const instance = (window as any).intlTelInputGlobals.getInstance(
      inputRef.current
    );
    const country = instance.getSelectedCountryData().iso2;
    onPhoneNumberUpdated({
      phoneNumber: e.target.value,
      phoneNumberCountryCode: country,
    });
  }

  return (
    <>
      <Global
        styles={
          countryCodeDisabled
            ? css`
                .iti__flag-container {
                  display: none;
                }
              `
            : null
        }
      />
      <input
        id={id}
        className={className}
        type="tel"
        placeholder={placeholder}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...mergedInputProps}
        ref={inputRef}
        onFocus={(e) => {
          handleFocusBehindBanner(e);
          userOnFocus?.(e);
        }}
        onChange={(e) => {
          changeHandler(e);
          userOnChange?.(e);
        }}
        onKeyDown={(e) => {
          userOnKeyDown?.(e);
        }}
        onPaste={(e) => {
          userOnPaste?.(e);
        }}
      />
    </>
  );
}

const IntlTelInput = ({
  id,
  className,
  placeholder,
  onPhoneNumberUpdated,
  countryCode = getCountryCode(),
  countryCodeDisabled = false,
  restrictUsPhoneInput = false,
  errorMessage,
  value,
  ...inputProps
}: IntlTelInputProps) => {
  const isUsRestricted = restrictUsPhoneInput && countryCode === "US";

  const {
    onChange: userOnChange,
    onKeyDown: userOnKeyDown,
    onPaste: userOnPaste,
    onFocus: userOnFocus,
    maxLength: userMaxLength,
    inputMode: userInputMode,
    defaultValue: _ignoredDefaultValue,
    ...restInputProps
  } = inputProps;

  if (isUsRestricted) {
    return (
      <UsRestrictedPhoneInput
        id={id}
        className={className}
        placeholder={placeholder}
        onPhoneNumberUpdated={onPhoneNumberUpdated}
        value={value == null ? undefined : String(value)}
        userOnChange={userOnChange}
        userOnKeyDown={userOnKeyDown}
        userOnPaste={userOnPaste}
        userOnFocus={userOnFocus}
        userMaxLength={userMaxLength}
        userInputMode={userInputMode}
        errorMessage={errorMessage}
        restInputProps={restInputProps}
      />
    );
  }

  const mergedInputProps = {
    ...restInputProps,
    ...(value !== undefined && value !== null ? { value } : {}),
    ...(userMaxLength !== undefined ? { maxLength: userMaxLength } : {}),
    ...(userInputMode !== undefined ? { inputMode: userInputMode } : {}),
  } as InputHTMLAttributes<HTMLInputElement>;

  return (
    <IntlTelPluginInput
      id={id}
      className={className}
      placeholder={placeholder}
      onPhoneNumberUpdated={onPhoneNumberUpdated}
      countryCode={countryCode}
      countryCodeDisabled={countryCodeDisabled}
      mergedInputProps={mergedInputProps}
      userOnChange={userOnChange}
      userOnKeyDown={userOnKeyDown}
      userOnPaste={userOnPaste}
      userOnFocus={userOnFocus}
    />
  );
};

export default IntlTelInput;
